import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';

const ColorFormModal = ({ isOpen, onClose, onSave, editColor = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    hexCode: '#000000',
    group: '',
    swatchImage: null
  });
  const [errors, setErrors] = useState({});
  const [previewColor, setPreviewColor] = useState('#000000');

  useEffect(() => {
    if (editColor) {
      setFormData({
        name: editColor?.name,
        hexCode: editColor?.hexCode,
        group: editColor?.group || '',
        swatchImage: editColor?.swatchImage || null
      });
      setPreviewColor(editColor?.hexCode);
    } else {
      setFormData({
        name: '',
        hexCode: '#000000',
        group: '',
        swatchImage: null
      });
      setPreviewColor('#000000');
    }
    setErrors({});
  }, [editColor, isOpen]);

  const groupOptions = [
    { value: '', label: 'No Group' },
    { value: 'base-colors', label: 'Base Colors' },
    { value: 'flake-colors', label: 'Flake Colors' },
    { value: 'metallic', label: 'Metallic' },
    { value: 'seasonal', label: 'Seasonal' },
    { value: 'popular', label: 'Popular' },
    { value: 'custom', label: 'Custom' }
  ];

  const validateHexCode = (hex) => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexRegex?.test(hex);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'hexCode') {
      if (validateHexCode(value)) {
        setPreviewColor(value);
        setErrors(prev => ({ ...prev, hexCode: '' }));
      } else {
        setErrors(prev => ({ ...prev, hexCode: 'Invalid hex code format' }));
      }
    }
    
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = 'Color name is required';
    }
    if (!validateHexCode(formData?.hexCode)) {
      newErrors.hexCode = 'Valid hex code is required';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave({
      ...formData,
      id: editColor?.id || Date.now(),
      usageCount: editColor?.usageCount || 0,
      createdAt: editColor?.createdAt || new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    });
    onClose();
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, swatchImage: reader?.result }));
    };
    reader?.readAsDataURL(file);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            {editColor ? 'Edit Color' : 'Add New Color'}
          </h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input
                label="Color Name"
                type="text"
                placeholder="e.g., Ocean Blue"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <div className="space-y-2">
                <Input
                  label="Hex Code"
                  type="text"
                  placeholder="#000000"
                  value={formData?.hexCode}
                  onChange={(e) => handleChange('hexCode', e?.target?.value)}
                  error={errors?.hexCode}
                  required
                />
                <input
                  type="color"
                  value={previewColor}
                  onChange={(e) => handleChange('hexCode', e?.target?.value)}
                  className="w-full h-12 rounded-lg border border-border cursor-pointer"
                />
              </div>

              <Select
                label="Color Group"
                description="Optional: Organize colors by category"
                options={groupOptions}
                value={formData?.group}
                onChange={(value) => handleChange('group', value)}
              />
            </div>

            <div className="space-y-4">
              <div className="bg-muted border border-border rounded-lg p-4">
                <h3 className="text-sm font-medium text-text-primary mb-3">
                  Color Preview
                </h3>
                <div
                  className="w-full h-32 rounded-lg border-2 border-border"
                  style={{ backgroundColor: previewColor }}
                />
                <div className="mt-3 text-center">
                  <code className="text-sm text-text-secondary font-mono">
                    {previewColor}
                  </code>
                </div>
              </div>

              <FileUpload
                label="Swatch Image (Optional)"
                description="Upload texture or pattern overlay"
                accept="image/*"
                maxSize={5242880}
                onUpload={handleFileUpload}
                error={errors?.swatchImage || ''}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              iconSize={16}
            >
              {editColor ? 'Update Color' : 'Add Color'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ColorFormModal;